import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, UserX, Clock, Calendar, Building2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AbsentTodayPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    loadAbsentEmployees();
  }, []);

  const loadAbsentEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/hr/attendance/absent-today`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      console.error('Failed to load absent employees:', error);
      toast.error('Failed to load absent employees');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-wt-gold" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/hr')}
          className="text-wt-text-muted hover:text-wt-text-primary"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to HR Dashboard
        </Button>
      </div>

      <div>
        <h1 className="text-2xl font-bold text-wt-text-primary flex items-center gap-2">
          <UserX className="h-6 w-6 text-red-400" />
          Absent Today
        </h1>
        <p className="text-wt-text-muted mt-1">
          {data?.date} - {data?.absent_count || 0} employee(s) absent
        </p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-wt-surface border border-wt-border rounded-lg p-4">
          <p className="text-sm text-wt-text-muted">Total Employees</p>
          <p className="text-2xl font-bold text-wt-text-primary">{data?.total_employees || 0}</p>
        </div>
        <div className="bg-wt-surface border border-wt-border rounded-lg p-4">
          <p className="text-sm text-wt-text-muted">Checked In</p>
          <p className="text-2xl font-bold text-green-400">{data?.checked_in_count || 0}</p>
        </div>
        <div className="bg-wt-surface border border-wt-border rounded-lg p-4">
          <p className="text-sm text-wt-text-muted">On Leave</p>
          <p className="text-2xl font-bold text-blue-400">{data?.on_leave_count || 0}</p>
        </div>
        <div className="bg-wt-surface border border-wt-border rounded-lg p-4">
          <p className="text-sm text-wt-text-muted">Week Off</p>
          <p className="text-2xl font-bold text-gray-400">{data?.week_off_count || 0}</p>
        </div>
        <div className="bg-wt-surface border border-wt-border rounded-lg p-4">
          <p className="text-sm text-wt-text-muted">Absent</p>
          <p className="text-2xl font-bold text-red-400">{data?.absent_count || 0}</p>
        </div>
      </div>

      {/* Holiday Banner */}
      {data?.is_holiday && (
        <div className="bg-purple-500/20 border border-purple-500 rounded-lg p-4 text-center">
          <p className="text-purple-400 font-medium">Today is a Holiday - No attendance required</p>
        </div>
      )}

      {/* Absent Employees List */}
      <div className="bg-wt-surface border border-wt-border rounded-lg">
        <div className="p-4 border-b border-wt-border">
          <h2 className="font-semibold text-wt-text-primary">Absent Employees</h2>
        </div>
        
        {data?.absent_employees?.length === 0 ? (
          <div className="p-8 text-center">
            <Clock className="h-12 w-12 mx-auto text-wt-text-muted mb-4 opacity-50" />
            <p className="text-wt-text-muted">All employees have checked in or are on leave today!</p>
          </div>
        ) : (
          <div className="divide-y divide-wt-border">
            {data?.absent_employees?.map((emp) => (
              <div key={emp.id} className="p-4 flex items-center justify-between hover:bg-wt-paper transition-colors">
                <div className="flex items-center gap-4">
                  <div className="h-10 w-10 rounded-full bg-red-500/20 flex items-center justify-center">
                    <span className="text-red-400 font-medium">
                      {emp.full_name?.charAt(0)?.toUpperCase() || '?'}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-wt-text-primary">{emp.full_name}</p>
                    <p className="text-sm text-wt-text-muted">{emp.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-sm text-wt-text-muted">
                  {emp.department && (
                    <div className="flex items-center gap-1">
                      <Building2 className="h-4 w-4" />
                      {emp.department}
                    </div>
                  )}
                  {emp.designation && (
                    <span className="px-2 py-1 rounded bg-wt-paper text-wt-text-muted">
                      {emp.designation}
                    </span>
                  )}
                  <span className="px-2 py-1 rounded bg-red-500/20 text-red-400 font-medium">
                    Absent
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AbsentTodayPage;
